import React, { useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Newspaper, XCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Card, SectionHeader } from "./shared";

const MARKETS = [
  { key: "FR-MRU", label: "France → Maurice" },
  { key: "GB-MU", label: "Royaume-Uni → Maurice" },
  { key: "DE-MU", label: "Allemagne → Maurice" },
  { key: "US-MU", label: "États-Unis → Maurice" },
  { key: "IN-MU", label: "Inde → Maurice" },
  { key: "AE-MU", label: "Émirats arabes unis → Maurice" },
  { key: "JP-MU", label: "Japon → Maurice" },
];

const MARKET_REQUEST_SPACING_MS = 8000;

const waitBeforeNextMarket = () => new Promise((resolve) => {
  window.setTimeout(resolve, MARKET_REQUEST_SPACING_MS);
});

export default function AdminNewsSignals() {
  const [selected, setSelected] = useState(() => MARKETS.map((market) => market.key));
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(null);

  const selectedLabels = useMemo(
    () => MARKETS.filter((market) => selected.includes(market.key)),
    [selected],
  );

  const toggle = (marketKey) => {
    setSelected((current) => current.includes(marketKey)
      ? current.filter((key) => key !== marketKey)
      : [...current, marketKey]);
  };

  const runRefresh = async () => {
    setRunning(true);
    setError("");
    setResults([]);
    setProgress({ completed: 0, total: selected.length, current: selected[0] });
    try {
      const nextResults = [];
      for (let index = 0; index < selected.length; index += 1) {
        const market = selected[index];
        if (index > 0) {
          // External providers rate-limit bursts even when each market is requested
          // through a separate application call. Spacing requests keeps the refresh
          // resilient without holding a single backend request open.
          await waitBeforeNextMarket();
        }
        setProgress({ completed: index, total: selected.length, current: market });
        try {
          // One request per market stays below the production proxy timeout and
          // lets later markets continue when a provider throttles one origin.
          const { data } = await api.post("/news-signals/admin/refresh", { markets: [market] });
          nextResults.push(data.results?.[0] || {
            status: "failed",
            market_key: market,
            error: "Réponse vide du serveur",
          });
        } catch (err) {
          nextResults.push({
            status: "failed",
            market_key: market,
            error: formatApiError(err?.response?.data?.detail, "L'actualisation a échoué."),
          });
        }
        setResults([...nextResults]);
        setProgress({
          completed: index + 1,
          total: selected.length,
          current: selected[index + 1] || null,
        });
      }
      setConfirming(false);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail, "L'actualisation a échoué."));
    } finally {
      setRunning(false);
      setProgress(null);
    }
  };

  return (
    <div data-testid="admin-news-signals">
      <SectionHeader
        title="News / Market Signals"
        subtitle="Actualisation manuelle de la veille sectorielle par marché émetteur. Accès réservé aux administrateurs plateforme."
      />

      <Card className="p-5">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-[#F1F7F3] p-2 text-[#3E7256]"><Newspaper size={20} /></div>
          <div>
            <h3 className="font-semibold text-[#17201A]">Marchés à actualiser</h3>
            <p className="mt-1 text-sm text-slate-500">
              Chaque marché crée un snapshot v2 indépendant, limité aux sources du pays émetteur.
              Les snapshots précédents restent disponibles pour audit.
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-2 sm:grid-cols-2" data-testid="news-market-list">
          {MARKETS.map((market) => (
            <label key={market.key} className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm hover:bg-slate-50">
              <input
                type="checkbox"
                checked={selected.includes(market.key)}
                onChange={() => toggle(market.key)}
                disabled={running}
                data-testid={`news-market-${market.key}`}
                className="h-4 w-4 rounded border-slate-300 text-[#1E3D2B]"
              />
              <span className="font-medium text-slate-700">{market.label}</span>
              <span className="ml-auto text-xs text-slate-400">{market.key}</span>
            </label>
          ))}
        </div>

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            disabled={selected.length === 0 || running}
            onClick={() => setConfirming(true)}
            data-testid="news-refresh-open-confirm"
            className="rounded-lg bg-[#1E3D2B] px-4 py-2 text-sm font-semibold text-white hover:bg-[#315C43] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Préparer l'actualisation ({selected.length})
          </button>
        </div>
      </Card>

      {confirming && (
        <Card className="mt-4 border-amber-200 bg-amber-50 p-5" data-testid="news-refresh-confirmation">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="mt-0.5 shrink-0 text-amber-700" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-950">Confirmer l'actualisation</h3>
              <p className="mt-1 text-sm text-amber-900">
                Cette action interrogera GNews lorsque le marché est pris en charge,
                puis GDELT en complément ou en repli, avant de publier un nouveau snapshot
                pour {selected.length} marché{selected.length > 1 ? "s" : ""}.
                Elle ne supprime pas les snapshots existants.
              </p>
              <ul className="mt-3 list-disc pl-5 text-sm text-amber-900">
                {selectedLabels.map((market) => <li key={market.key}>{market.label} ({market.key})</li>)}
              </ul>
              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={runRefresh}
                  disabled={running}
                  data-testid="news-refresh-confirm"
                  className="inline-flex items-center gap-2 rounded-lg bg-amber-800 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-900 disabled:opacity-60"
                >
                  {running && <Loader2 size={15} className="animate-spin" />}
                  {running && progress
                    ? `Actualisation ${Math.min(progress.completed + 1, progress.total)}/${progress.total}…`
                    : "Confirmer et actualiser"}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  disabled={running}
                  data-testid="news-refresh-cancel"
                  className="rounded-lg border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-60"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {error && (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert" data-testid="news-refresh-error">
          <XCircle size={18} /> {error}
        </div>
      )}

      {results && (
        <Card className="mt-4 p-5" data-testid="news-refresh-results">
          <h3 className="font-semibold text-[#17201A]">Résultat de l'actualisation</h3>
          <div className="mt-3 space-y-2">
            {results.map((result) => (
              <div key={result.market_key} className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm">
                {result.status === "ready"
                  ? <CheckCircle2 size={18} className="text-emerald-600" />
                  : <XCircle size={18} className="text-red-600" />}
                <span className="font-semibold text-slate-700">{result.market_key}</span>
                <span className="text-slate-500">
                  {result.status === "ready"
                    ? `${result.articles} article${result.articles === 1 ? "" : "s"}${result.provider ? ` · ${result.provider}` : ""}`
                    : result.error || "Échec"}
                </span>
                <span className={`ml-auto rounded-full px-2 py-0.5 text-xs font-semibold ${result.status === "ready" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                  {result.status === "ready" ? "Publié" : "Échec"}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
